import { PolygonFilter } from "models/filter";

// Shared helpers for converting the committed well polygon filter into
// plain GeoJSON geometry. Used by both `RigFracDataTableWidget` and the
// rig/frac map layer filter (`useRigFracLayerFilter`) so the two stay in
// lockstep on how a committed polygon scopes rigs/frac crews.

/**
 * Whether `coordinates` is nested as a MultiPolygon rather than a Polygon.
 * `PolygonFilter.type` is a generic `string` not guaranteed to actually say
 * "Polygon"/"MultiPolygon", so shape is detected from nesting depth instead.
 */
function isMultiPolygonCoordinates(
  coordinates: number[][][] | number[][][][]
): coordinates is number[][][][] {
  return Array.isArray(coordinates[0]?.[0]?.[0]);
}

/** A `PolygonFilter`'s `coordinates` as a plain GeoJSON `Polygon`/`MultiPolygon` geometry. */
export function polygonFilterToGeometry(
  polygonFilter: PolygonFilter
): GeoJSON.Polygon | GeoJSON.MultiPolygon {
  const coordinates = polygonFilter.coordinates;
  if (isMultiPolygonCoordinates(coordinates)) {
    return { type: "MultiPolygon", coordinates };
  }
  return { type: "Polygon", coordinates };
}

/**
 * Unions one or more exclude `PolygonFilter`s into a single GeoJSON
 * `MultiPolygon` by concatenating each entry's polygon coordinates as
 * MultiPolygon members. Returns `undefined` when there are no exclude
 * polygons.
 */
export function unionExcludePolygons(
  excludePolygonsFilter: PolygonFilter[]
): Maybe<GeoJSON.MultiPolygon> {
  if (!excludePolygonsFilter.length) {
    return undefined;
  }

  const members: number[][][][] = [];
  excludePolygonsFilter.forEach((polygonFilter) => {
    const coordinates = polygonFilter.coordinates;
    if (isMultiPolygonCoordinates(coordinates)) {
      members.push(...coordinates);
    } else {
      members.push(coordinates);
    }
  });

  return { type: "MultiPolygon", coordinates: members };
}

/**
 * Whether a `Polygon`/`MultiPolygon` geometry has at least one non-empty
 * ring. Guards against handing mapbox-gl's `within` expression a degenerate
 * (empty-coordinates) geometry, which throws.
 */
export function hasPolygonCoordinates(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
): boolean {
  if (geometry.type === "Polygon") {
    return geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0;
  }
  return geometry.coordinates.some(
    (polygon) => polygon.length > 0 && polygon[0].length > 0
  );
}

/** Whether a `polygonFilter` is a real committed lasso (vs. the viewport-driven default). */
export function isCommittedSelectionPolygon(
  polygonFilter: Maybe<PolygonFilter>
): polygonFilter is PolygonFilter {
  return !!polygonFilter && polygonFilter.source === "selection";
}
